import React, { useState } from "react";
import Papa from "papaparse";
import ModalView from "@modals/modalview/ModalView";
import fileIcon from "@assets/icons/file-upload.svg";
import incompleteIcon from "@assets/icons/incomplete.svg";
import completedIcon from "@assets/icons/completed.svg";
import styles from "./StudentListUpload.module.css";
import { useDispatch } from "react-redux";
import { setSessionStudentUploadData } from "@store/admin/AdminDashboardStore";
function StudentListUpload({
  isOpen,
  examSessionId,
  questionPaperId,
  handleSave,
  onClose,
}: {
  isOpen: boolean;
  examSessionId: any;
  questionPaperId: any;
  handleSave: () => void;
  onClose: () => void;
}) {
  const [uploadStatus, setUploadStatus] = useState<
    "idle" | "uploading" | "completed" | "error"
  >("idle");
  const dispatch = useDispatch();
  const handleUploadStudentData = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const MAX_FILE_SIZE = 20 * 1024 * 1024;
    const errors: string[] = [];

    setUploadStatus("uploading");

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        try {
          const formattedData = results.data
            .map((row: any, index: number) => {
              const student_identifier = row["student_identifier"]?.trim();
              const pdf_file_name = row["pdf_file_name"]?.trim();

              if (!student_identifier || !pdf_file_name) {
                errors.push(
                  `Row ${
                    index + 2
                  }: Missing student_identifier or pdf_file_name`
                );
                return null;
              }

              return { student_identifier, pdf_file_name };
            })
            .filter(Boolean) as {
            student_identifier: string;
            pdf_file_name: string;
          }[];


          const studentIds = formattedData.map((d) => d.student_identifier);
          const duplicateStudents = studentIds.filter(
            (id, idx) => studentIds.indexOf(id) !== idx
          );
          if (duplicateStudents.length > 0) {
            errors.push(
              `Duplicate student_identifier(s) found: ${[
                ...new Set(duplicateStudents),
              ].join(", ")}`
            );
          }

          const fileNames = formattedData.map((d) => d.pdf_file_name);
          const duplicateFiles = fileNames.filter(
            (name, idx) => fileNames.indexOf(name) !== idx
          );
          if (duplicateFiles.length > 0) {
            errors.push(
              `Duplicate pdf_file_name(s) found: ${[
                ...new Set(duplicateFiles),
              ].join(", ")}`
            );
          }

          if (
            formattedData.length > 0 &&
            new Set(studentIds).size !== new Set(fileNames).size
          ) {
            errors.push(`Each student must have a unique file and vice versa.`);
          }

          if (errors.length > 0) {
            console.error("Validation Errors:", errors);
            setUploadStatus("error");
            return;
          }
          localStorage.setItem(
            "studentFileDetails",
            JSON.stringify(formattedData)
          );
          dispatch(setSessionStudentUploadData(formattedData));
          setUploadStatus("completed");
        } catch (error) {
          console.error("Error parsing CSV:", error);
          setUploadStatus("error");
        }
      },
      error: (err) => {
        console.error("PapaParse Error:", err);
        setUploadStatus("error");
      },
    });
  };

  return (
    <ModalView isOpen={isOpen} onClose={onClose}>
      <div className={styles.studentListUploadContainer}>
        <div className={styles.header}>
          <h2>Import Files</h2>
        </div>
        <div className={styles.content}>
          {uploadStatus === "idle" && (
            <div className={styles.fileUploadContainer}>
              <div className={styles.fileUpload} style={{ display: "flex" }}>
                <form className={styles.form}>
                  <input
                    type="file"
                    accept=".csv"
                    id="fileUpload"
                    className={styles.fileInput}
                    onChange={handleUploadStudentData}
                  />
                  <label htmlFor="fileUpload" className={styles.fileLabel}>
                    UPLOAD FILE
                    <img src={fileIcon} alt="Upload" />
                  </label>
                </form>
                <div className={styles.fileInfo}>
                  <p>
                    Please{" "}
                    <a href="src/assets/csvs/studentListUploadTemplate.csv" download>
                      click here
                    </a>{" "}
                    to download a template for .csv file format
                  </p>
                  <p>File size must be less than 20MB</p>
                  <p>Supported format for upload - .csv</p>
                </div>
              </div>
            </div>
          )}
          {uploadStatus === "uploading" && (
            <div className={styles.fileUploading}>
              <p className={styles.uploadingText}>File Uploading...</p>
            </div>
          )}
          {uploadStatus === "completed" && (
            <div className={styles.fileUploaded}>
              <p className={styles.uploadingCompleted}>
                UPLOAD COMPLETED! <img src={completedIcon} alt="Completed" />
              </p>
            </div>
          )}
          {uploadStatus === "error" && (
            <div className={styles.fileUploadFailed}>
              <div className={styles.uploadingFailedContainer}>
                <p className={styles.uploadingFailed}>Oops! Upload Failed</p>
                <img src={incompleteIcon} alt="Incomplete" />
              </div>
              <p className={styles.errorMessage}>
                Student already enrolled, cannot enrol student in same batch.
              </p>
            </div>
          )}
        </div>
        <div className={styles.buttonContainer}>
          <button
            className={styles.cancelButton}
            onClick={() => {
              setUploadStatus("idle");
              onClose();
            }}
          >
            Cancel
          </button>
          <button
            className={styles.continueButton}
            onClick={() => {
              setUploadStatus("idle");
              handleSave();
            }}
            disabled={uploadStatus !== "completed"}
          >
            Save
          </button>
        </div>
      </div>
    </ModalView>
  );
}

export default StudentListUpload;
